import { log, assert, error } from '../../../app/logger.js';

/**
 * Map base class for maps API wrappers.
 */
const MapBaseMixin = EventedBase =>
  class extends EventedBase {
    constructor(el, options = {}) {
      super(options);

      const { center, zoom } = options;
      assert(el, `${this.constructor.name}: no element`);
      assert(center, `${this.constructor.name}: no map center`);
      assert(zoom, `${this.constructor.name}: no map zoom`);

      this.el = el;
      this.defaultCenter = center;
      this.defaultZoom = zoom;
    }

    get type() {
      return 'map';
    }

    retrieveBounds() {
      if (this.retrievingBounds) {
        log(
          `[map:bounds] reuse retrieveBounds promise: ${this.retrievingBounds}`
        );
        return this.retrieveBoundsPromise;
      }

      this.retrievingBounds = true;
      return (this.retrieveBoundsPromise = new Promise(resolve => {
        const rid = Math.random();
        log(`[map:bounds] retrieveBounds ${rid}`);

        // If map is moving, then bounds are undefined or squashed into a point.
        // Wait for bounds_changed event in such cases. Note, bounds_changed
        // doesn't guarantee the bounds are not squashed.
        // If map fails to fire bounds_changed event in reasonable time, then
        // fail.
        let bounds = this.bounds;
        if (this.constructor.isValidBounds(bounds)) {
          log(
            `[map:bounds] retrieveBounds ${rid}: bounds ok ${JSON.stringify(
              bounds
            )}`
          );
          this.retrievingBounds = false;
          resolve(bounds);
          return;
        }

        log(
          `[map:bounds] ${rid}: invalid bounds: ${JSON.stringify(
            bounds
          )}, wait for bounds_changed event`
        );

        let listener = this.on('bounds_changed', () => {
          log(`[map:bounds] retrieveBounds ${rid}: bounds_changed triggered`);
          let bounds = this.bounds;
          if (this.constructor.isValidBounds(bounds)) {
            log(
              `[map:bounds] retrieveBounds ${rid}: bounds_changed triggered, bounds ok`
            );
            this.off('bounds_changed', listener);
            window.clearTimeout(tid);
            this.retrievingBounds = false;
            resolve(bounds);
          }
        });

        let tid = window.setTimeout(() => {
          log(`[map:bounds] retrieveBounds ${rid}: map bounds timeout`);
          this.off('bounds_changed', listener);
          let bounds = this.bounds;
          if (this.constructor.isValidBounds(bounds)) {
            log(
              `[map:bounds] retrieveBounds ${rid}: map bounds timeout, but bounds ok`
            );
            this.retrievingBounds = false;
            resolve(bounds);
            return;
          }
          this.retrievingBounds = false;
          error(
            `[map:bounds] map retrieveBounds timeout, bounds: ${this.constructor.stringifyBounds(
              bounds
            )}`
          );
          resolve({ south: 0, east: 0, north: 0, west: 0 });
        }, 500);
      }));
    }

    /**
     * Returns bounds for the given points.
     */
    static boundsFor(points) {
      let lats = points.map(p => p.lat);
      let lngs = points.map(p => p.lng);
      return {
        north: Math.max.apply(Math, lats),
        south: Math.min.apply(Math, lats),
        east: Math.max.apply(Math, lngs),
        west: Math.min.apply(Math, lngs),
      };
    }

    static isValidBounds(b) {
      return (
        b &&
        !this.isBoundsSquashed(b) &&
        Math.abs(b.north) != Infinity &&
        Math.abs(b.south) != Infinity &&
        Math.abs(b.west) != Infinity &&
        Math.abs(b.east) != Infinity
      );
    }

    static isValidCoords(c) {
      return (
        c &&
        c.lat &&
        c.lng &&
        Math.abs(c.lat) != Infinity &&
        Math.abs(c.lng) != Infinity
      );
    }

    static isBoundsSquashed(b) {
      return b.north == b.south || b.west == b.east;
    }

    static stringifyBounds(b) {
      return (
        (b &&
          `{ south: ${b.south}, east: ${b.east}, north: ${b.north}, west: ${b.west} }`) ||
        null
      );
    }

    static getDefaultOptions() {
      return Object.assign(
        {
          name: 'map',
        },
        super.getDefaultOptions()
      );
    }
  };

export { MapBaseMixin };
